import { validateEmail } from "@/utils/validateEmail";
import classNames from "classnames";
import {
  ComponentProps,
  HTMLProps,
  RefObject,
  useEffect,
  useId,
  useState,
} from "react";

type InputFieldType = {
  type: ComponentProps<"input">["type"];
  placeholder?: string;
  name: string;
  helpText?: string;
  pattern?: string;
  defaultValue?: string;
  className?: HTMLProps<HTMLElement>["className"];
  containerClassName?: HTMLProps<HTMLElement>["className"];
  required?: boolean;
  readOnly?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  error?: string;
  // Bump this when the parent re-validates so an `error` whose text is the
  // same as last time is shown again (see suppressExternalError below).
  errorKey?: string | number;
  // Same purpose as errorKey, for callers that use a numeric version.
  errorVersion?: number;
  // Announce the error message itself via role="alert" (for forms
  // without a FormErrorSummary).
  alertOnError?: boolean;
} & HTMLProps<HTMLInputElement>;

const InputField = ({
  id,
  type,
  placeholder,
  helpText,
  className,
  containerClassName,
  name,
  pattern,
  defaultValue,
  required,
  readOnly,
  label,
  inputRef,
  error: externalError,
  errorKey,
  errorVersion,
  alertOnError,
  "aria-describedby": extraDescribedBy,
  ...rest
}: InputFieldType) => {
  const [internalError, setInternalError] = useState("");
  // externalError comes from the parent (typically re-validated only on
  // submit), so it can go stale: the user edits the value to fix the
  // reported problem, but the old message — now describing a problem that
  // no longer exists — stays on screen until the next submit. Once the
  // user edits the field again, suppress it in favor of internalError
  // (cleared by handleInput) until the parent sends a genuinely new
  // externalError value (or the parent bumps errorKey on a re-submit).
  const [suppressExternalError, setSuppressExternalError] = useState(false);
  useEffect(() => {
    setSuppressExternalError(false);
  }, [externalError, errorKey, errorVersion]);
  const error = suppressExternalError
    ? internalError
    : externalError || internalError;
  const setError = setInternalError;
  const helpTextId = useId();
  const inputId = useId();

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.preventDefault();

    const message =
      type === "email"
        ? validateEmail(e.currentTarget.value) ??
          "Ange en giltig e-postadress i formatet email@email.com."
        : type === "tel"
          ? "Ange ett giltigt mobilnummer utan landskod med 9 eller 10 siffror."
          : required
            ? "Detta fält måste fyllas i."
            : "Ogiltig inmatning.";

    e.currentTarget.setCustomValidity(message);
    setError(message);

    window.parent.postMessage(
      {
        name: "input-error",
        data: message,
      },
      "*",
    );
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    if (input.inputMode === "numeric") {
      input.value = input.value.replace(/\D/g, "");
    }

    input.setCustomValidity("");
    setError("");
    setSuppressExternalError(true);
  };

  return (
    <div className={classNames("relative", containerClassName)}>
      {label && (
        <label
          className="absolute px-4 pt-2 text-sm font-bold"
          htmlFor={id || inputId}
        >
          <span className="text-gray-400">{label}</span>{" "}
          {required && (
            <span className="">
              *<span className="sr-only">(Obligatoriskt)</span>
            </span>
          )}
        </label>
      )}

      <input
        id={id || inputId}
        aria-describedby={
          [extraDescribedBy, helpText || error?.trim() ? helpTextId : ""]
            .filter(Boolean)
            .join(" ") || undefined
        }
        aria-invalid={Boolean(error?.trim())}
        aria-errormessage={error?.trim() ? helpTextId : undefined}
        type={type}
        placeholder={placeholder}
        name={name}
        pattern={pattern}
        required={required}
        defaultValue={defaultValue}
        readOnly={readOnly}
        onInvalid={handleInvalid}
        onInput={handleInput}
        ref={inputRef}
        className={classNames(
          "text-[16px] bg-sand-200 rounded-sm text-gray-t00 pt-4 px-4 h-16 w-full border placeholder:text-gray-400 placeholder:opacity-75 read-only:bg-sand-600 read-only:text-gray-400 read-only:cursor-not-allowed",
          error ? "border-red-500" : "border-sand-600",
          className,
        )}
        {...rest}
      />

      {(helpText || error?.trim()) && (
        <p
          id={helpTextId}
          role={alertOnError && error?.trim() ? "alert" : undefined}
          className={classNames(
            "mt-0.5 ml-4 text-sm",
            error?.trim()
              ? "text-red-700 dark:text-static-white"
              : "text-gray-400",
          )}
        >
          {error?.trim() || helpText}
        </p>
      )}
    </div>
  );
};

export default InputField;
